// Per-(app, work-area) override for which view ('claude' chat panel or
// 'terminal') occupies the workspace main pane. Dependency-free so both
// WorkspaceLayout (desktop) and MobileWorkspaceLayout can share the same
// resolution/persistence logic.
//
// Scoped per WORK AREA, not per app: work areas are independent worlds (own
// worktree, conversation, claude consumer), so switching codex-parity to the
// CLI must not drag main to the CLI too — exactly like switching views in one
// app never affects another app.

export type MainView = 'claude' | 'terminal'

function storageKey(appName: string, workArea: string): string {
  return `workspace-main-view-${appName}::${workArea}`
}

// Pre-per-area storage key. Kept readable as a one-time default so an app's
// existing choice carries over to areas that haven't made their own yet;
// never written anymore, so per-area choices diverge from it independently.
function legacyStorageKey(appName: string): string {
  return `workspace-main-view-${appName}`
}

// Reads the per-area localStorage override (falling back to the legacy
// app-wide value). Returns null when unset, unreadable (private mode /
// storage disabled), or holding an unknown value.
export function loadMainViewOverride(appName: string, workArea: string): MainView | null {
  try {
    const raw = localStorage.getItem(storageKey(appName, workArea))
      ?? localStorage.getItem(legacyStorageKey(appName))
    if (raw === 'claude' || raw === 'terminal') return raw
  } catch {
    // Storage disabled / private mode — fall through to the setting default.
  }
  return null
}

export function persistMainView(appName: string, workArea: string, view: MainView): void {
  try {
    localStorage.setItem(storageKey(appName, workArea), view)
  } catch {
    // Ignore storage errors
  }
}

// Effective main view: an explicit override always wins; otherwise fall back
// to the global `claude_panel_enabled` setting's default.
export function resolveMainView(override: MainView | null, claudePanelEnabled: boolean): MainView {
  return override ?? (claudePanelEnabled ? 'claude' : 'terminal')
}
